import { ArrowLeft, Package, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import { ProductThumbnail } from '../components/ui/aspect-ratio-image';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Address, Shipment, ShipmentType } from '../types/shipment';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

interface ShipmentFormData {
  job_id: string;
  bl_number: string;
  shipment_type: ShipmentType;
  origin_address_id: string;
  destination_address_id: string;
  planned_departure_at: string;
  planned_arrival_at: string;
}

interface Job {
  id: string;
  job_number: string;
  order_id: string;
  order?: {
    order_number: string;
    customer_name: string;
  };
}

interface AssetInstance {
  id: string;
  instance_number: string;
  current_status: string;
  template?: {
    id: string;
    name: string;
    images?: Array<{ url?: string; variant: string }>;
  };
  template_id?: string;
  current_location?: {
    id: string;
    city: string;
    region: string;
  };
}

interface OrderItem {
  id: string;
  asset_template_id: string;
  quantity: number;
  asset_template?: {
    id: string;
    name: string;
    images?: Array<{ url?: string; variant: string }>;
  };
}

const EditShipment: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isCreateMode = location.pathname.endsWith('/new');
  const preSelectedJobId = (location.state as any)?.preSelectedJobId;

  const [user, setUser] = useState<User | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(!isCreateMode); // Don't show loading for create mode
  const [submitting, setSubmitting] = useState(false);
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [assetInstances, setAssetInstances] = useState<AssetInstance[]>([]);
  const [availableInstances, setAvailableInstances] = useState<AssetInstance[]>([]);
  const [addUnitsModalOpen, setAddUnitsModalOpen] = useState(false);
  const [selectedNewUnits, setSelectedNewUnits] = useState<string[]>([]);
  const [instancesToRemove, setInstancesToRemove] = useState<string[]>([]);
  const [modalPage, setModalPage] = useState(1);
  const [modalLimit, setModalLimit] = useState(10);
  const [modalTotalPages, setModalTotalPages] = useState(1);
  const [modalTotalItems, setModalTotalItems] = useState(0);
  const [selectUnitsForProduct, setSelectUnitsForProduct] = useState<OrderItem | null>(null);
  const [formData, setFormData] = useState<ShipmentFormData>({
    job_id: preSelectedJobId || '',
    bl_number: '',
    shipment_type: ShipmentType.OUTBOUND,
    origin_address_id: '',
    destination_address_id: '',
    planned_departure_at: '',
    planned_arrival_at: '',
  });

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }

    if (isCreateMode) {
      loadJobs();
      loadAddresses();
    } else if (id) {
      // Only load shipment data if we have a valid ID
      loadShipmentData();
      loadAddresses();
    }
  }, [id]);

  // Update job_id when preSelectedJobId becomes available
  useEffect(() => {
    if (isCreateMode && preSelectedJobId && !formData.job_id) {
      setFormData(prev => ({ ...prev, job_id: preSelectedJobId }));
    }
  }, [preSelectedJobId, isCreateMode]);

  // Load order items and available instances when job is selected in create mode
  useEffect(() => {
    if (isCreateMode && formData.job_id) {
      loadOrderItemsForJob(formData.job_id);
    }
  }, [isCreateMode, formData.job_id]);

  // Auto-generate BL number when shipment type is selected in create mode
  useEffect(() => {
    if (isCreateMode && formData.shipment_type && !formData.bl_number) {
      generateBLNumber(formData.shipment_type);
    }
  }, [isCreateMode, formData.shipment_type]);

  // Reload available instances when modal pagination changes
  useEffect(() => {
    if (addUnitsModalOpen) {
      const productId = selectUnitsForProduct?.asset_template_id;
      loadAvailableInstances(modalPage, modalLimit, productId);
    }
  }, [modalPage, modalLimit, addUnitsModalOpen, selectUnitsForProduct]);

  const loadOrderItemsForJob = async (jobId: string) => {
    try {
      const token = localStorage.getItem('token');

      // Get the job to find the order_id
      const jobResponse = await fetch(`/api/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const jobData = await jobResponse.json();

      if (jobData.success && jobData.data.order_id) {
        const orderId = jobData.data.order_id;

        // Load order items
        const orderItemsResponse = await fetch(`/api/orders/${orderId}/items`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const orderItemsData = await orderItemsResponse.json();
        if (orderItemsData.success) {
          setOrderItems(orderItemsData.data);
        }

        // Prefill destination address from the order's delivery address (editable afterwards)
        try {
          const orderResponse = await fetch(`/api/orders/${orderId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const orderData = await orderResponse.json();
          if (orderData?.success) {
            const deliveryAddressId = orderData.data?.delivery_address_id || '';
            setFormData(prev => ({
              ...prev,
              destination_address_id: prev.destination_address_id || deliveryAddressId || '',
            }));
          }
        } catch (e) {
          // Non-fatal: if order load fails, user can still pick manually
          console.warn('Failed to prefill destination address from order', e);
        }

        // Load available instances for products in the order
        loadAvailableInstances();
      }
    } catch (err) {
      console.error('Error loading order items for job:', err);
    }
  };

  const loadJobs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/jobs', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to load jobs');
      }

      const data = await response.json();
      const jobsData = Array.isArray(data.data) ? data.data : (data.data?.jobs || []);
      setJobs(data.success ? jobsData : []);
    } catch (err) {
      console.error('Error loading jobs:', err);
      setJobs([]);
    }
  };

  const generateBLNumber = async (shipmentType: ShipmentType) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/shipments/generate-bl-number?shipment_type=${shipmentType}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to generate BL number');
      }

      const data = await response.json();
      if (data.success && data.data.bl_number) {
        setFormData(prev => ({ ...prev, bl_number: data.data.bl_number }));
      }
    } catch (err) {
      console.error('Error generating BL number:', err);
    }
  };

    const loadShipmentData = async () => {
    // Don't load if id is invalid
    if (!id) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/shipments/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();

      if (data.success) {
        const shipmentData = data.data;
        setShipment(shipmentData);
        setAssetInstances(shipmentData.asset_instances || []);

        // Extract job_id if available (for reference, though not used in edit mode)
        const jobId = shipmentData.jobs && shipmentData.jobs.length > 0 ? shipmentData.jobs[0].id : '';

        setFormData({
          job_id: jobId,
          bl_number: shipmentData.bl_number,
          shipment_type: shipmentData.shipment_type,
          origin_address_id: shipmentData.origin_address_id || '',
          destination_address_id: shipmentData.destination_address_id || '',
          planned_departure_at: shipmentData.planned_departure_at ? shipmentData.planned_departure_at.split('.')[0] : '',
          planned_arrival_at: shipmentData.planned_arrival_at ? shipmentData.planned_arrival_at.split('.')[0] : '',
        });

        // Load order items if shipment has jobs
        if (shipmentData.jobs && shipmentData.jobs.length > 0) {
          const orderId = shipmentData.jobs[0].order_id;
          console.log('[EditShipment] Loading order items for order:', orderId);
          if (orderId) {
            const orderItemsResponse = await fetch(`/api/orders/${orderId}/items`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const orderItemsData = await orderItemsResponse.json();
            console.log('[EditShipment] Order items response:', orderItemsData);
            if (orderItemsData.success) {
              setOrderItems(orderItemsData.data || []);
              console.log('[EditShipment] Set order items:', orderItemsData.data);
            } else {
              console.warn('[EditShipment] Failed to load order items:', orderItemsData);
              setOrderItems([]);
            }
          } else {
            console.warn('[EditShipment] Job has no order_id');
            setOrderItems([]);
          }
        } else {
          console.warn('[EditShipment] Shipment has no jobs');
          setOrderItems([]);
        }
      }
    } catch (err) {
      console.error('Error loading shipment:', err);
      alert('Failed to load shipment data');
      navigate('/shipments');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableInstances = async (page = modalPage, limit = modalLimit, productId?: string) => {
    try {
      const token = localStorage.getItem('token');

      // Fetch available units, optionally filtered by template_id
      const templateParam = productId ? `&template_id=${productId}` : '';
      const response = await fetch(`/api/asset-instances?status=available&page=${page}&limit=${limit}${templateParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (data.success && data.pagination) {
        setAvailableInstances(data.data || []);
        setModalTotalItems(data.pagination.total);
        setModalTotalPages(data.pagination.pages);
      } else {
        const allAvailable = data.success ? data.data : [];
        setAvailableInstances(allAvailable);
        setModalTotalItems(allAvailable.length);
        setModalTotalPages(1);
      }
    } catch (err) {
      console.error('Error loading available instances:', err);
    }
  };

  // Calculate how many more units of each product can be added
  const getAvailableQuantityForProduct = (templateId: string) => {
    const orderItem = orderItems.find(item => item.asset_template_id === templateId);
    if (!orderItem) return 0;

    const currentCount = assetInstances.filter(
      inst => (inst.template?.id || inst.template_id) === templateId
    ).length;

    return Math.max(0, orderItem.quantity - currentCount);
  };

  // Check if we can select more units of this product
  const canSelectProduct = (templateId: string) => {
    const available = getAvailableQuantityForProduct(templateId);
    const alreadySelected = selectedNewUnits.filter(id => {
      const instance = availableInstances.find(inst => inst.id === id);
      return (instance?.template?.id || instance?.template_id) === templateId;
    }).length;

    return (alreadySelected < available);
  };

  const handleAddUnits = async () => {
    if (selectedNewUnits.length === 0) return;

    // In create mode, just update local state - units will be added on submit
    if (isCreateMode) {
      // Convert selected IDs to asset instances for display
      const selectedInstances = availableInstances.filter(inst =>
        selectedNewUnits.includes(inst.id)
      );
      setAssetInstances([...assetInstances, ...selectedInstances]);
      // Keep selectedNewUnits for when we submit the form
      setSelectedNewUnits([]);
      setSelectUnitsForProduct(null);
      setAddUnitsModalOpen(false);
      return;
    }

    // In edit mode, add units via API
    try {
      const token = localStorage.getItem('token');

      for (const instanceId of selectedNewUnits) {
        const response = await fetch(`/api/shipments/${id}/assets`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ asset_instance_id: instanceId }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to add unit');
        }
      }

      // Reload shipment data
      await loadShipmentData();
      setSelectedNewUnits([]);
      setSelectUnitsForProduct(null);
      setAddUnitsModalOpen(false);
    } catch (err) {
      console.error('Error adding units:', err);
      alert(err instanceof Error ? err.message : 'Failed to add units');
    }
  };

  const handleRemoveUnit = async (instanceId: string) => {
    // In create mode, just update local state
    if (isCreateMode) {
      setAssetInstances(prev => prev.filter(inst => inst.id !== instanceId));
      setSelectedNewUnits(prev => prev.filter(id => id !== instanceId));
      return;
    }

    // In edit mode, remove via API
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/shipments/${id}/assets/${instanceId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      // Update local state
      setAssetInstances(prev => prev.filter(inst => inst.id !== instanceId));
    } catch (err) {
      console.error('Error removing unit:', err);
      alert('Failed to remove unit');
    }
  };

  const openAddUnitsModal = () => {
    console.log('[EditShipment] Opening Add Units modal');
    console.log('[EditShipment] Order items count:', orderItems.length);
    console.log('[EditShipment] Order items:', orderItems);
    setSelectUnitsForProduct(null); // No specific product selected
    setModalPage(1);
    setSelectedNewUnits([]);
    setAddUnitsModalOpen(true);
  };

  const openSelectUnitsForProduct = (product: OrderItem) => {
    setSelectUnitsForProduct(product);
    setModalPage(1);
    setSelectedNewUnits([]);
    setAddUnitsModalOpen(true);
  };

  const loadAddresses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/addresses', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setAddresses(Array.isArray(data) ? data : (data.data || []));
    } catch (err) {
      console.error('Error loading addresses:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');

      const payload = {
        bl_number: formData.bl_number,
        shipment_type: formData.shipment_type,
        origin_address_id: formData.origin_address_id || null,
        destination_address_id: formData.destination_address_id || null,
        planned_departure_at: formData.planned_departure_at || null,
        planned_arrival_at: formData.planned_arrival_at || null,
      };

      if (isCreateMode) {
        // Validate job selection
        if (!formData.job_id) {
          throw new Error('Please select a job');
        }

        // Step 1: Create the shipment
        const shipmentResponse = await fetch('/api/shipments', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        const shipmentData = await shipmentResponse.json();

        if (!shipmentResponse.ok) {
          throw new Error(shipmentData.message || 'Failed to create shipment');
        }

        // Step 2: Link the shipment to the job
        const linkResponse = await fetch(`/api/jobs/${formData.job_id}/shipments`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            shipment_id: shipmentData.data.id,
          }),
        });

        if (!linkResponse.ok) {
          const linkData = await linkResponse.json();
          throw new Error(`Shipment created but failed to link to job: ${linkData.message || 'Unknown error'}`);
        }

        // Step 3: Add selected product units to the shipment (if any)
        if (assetInstances.length > 0) {
          for (const instance of assetInstances) {
            try {
              await fetch(`/api/shipments/${shipmentData.data.id}/assets`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ asset_instance_id: instance.id }),
              });
            } catch (err) {
              console.error('Error adding unit to shipment:', err);
              // Continue with other units even if one fails
            }
          }
        }

        navigate(`/shipments/${shipmentData.data.id}`);
      } else {
        // Update existing shipment
        const response = await fetch(`/api/shipments/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.message || 'Failed to update shipment');
        }

        navigate(`/shipments/${id}`);
      }
    } catch (error) {
      console.error(isCreateMode ? 'Error creating shipment:' : 'Error updating shipment:', error);
      alert(error instanceof Error ? error.message : `Failed to ${isCreateMode ? 'create' : 'update'} shipment`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field: keyof ShipmentFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatAddressLabel = (address: Address) => {
    return `${address.address_line1}, ${address.city}${address.postal_code ? ` ${address.postal_code}` : ''}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-12">Loading...</div>
        </main>
      </div>
    );
  }

  if (!isCreateMode && !shipment) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-12">Shipment not found</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/shipments')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Shipments
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold">
              {isCreateMode ? 'Create New Shipment' : `Edit Shipment ${shipment?.bl_number}`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Job Selection (Create Mode Only) */}
              {isCreateMode && (
                <div>
                  <Label htmlFor="job_id">Job *</Label>
                  <Select
                    value={formData.job_id}
                    onValueChange={(value) => handleChange('job_id', value)}
                    disabled={submitting || !!preSelectedJobId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a job" />
                    </SelectTrigger>
                    <SelectContent>
                      {jobs.length > 0 ? (
                        jobs.map((job) => (
                          <SelectItem key={job.id} value={job.id}>
                            {job.job_number}
                            {job.order && ` - Order: ${job.order.order_number} (${job.order.customer_name})`}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                          No jobs available
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                  {preSelectedJobId ? (
                    <p className="text-xs text-muted-foreground mt-1">
                      Job pre-selected from job details page
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-1">
                      Select the job that this shipment is for
                    </p>
                  )}
                </div>
              )}

              {/* BL Number and Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="bl_number">BL Number *</Label>
                  {isCreateMode ? (
                    <div className="flex gap-2">
                      <Input
                        id="bl_number"
                        value={formData.bl_number}
                        readOnly
                        required
                        className="bg-muted"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => generateBLNumber(formData.shipment_type)}
                        disabled={!formData.shipment_type}
                      >
                        Regenerate
                      </Button>
                    </div>
                  ) : (
                    <Input
                      id="bl_number"
                      value={formData.bl_number}
                      onChange={(e) => handleChange('bl_number', e.target.value)}
                      required
                    />
                  )}
                </div>

                <div>
                  <Label htmlFor="shipment_type">Shipment Type *</Label>
                  <Select
                    value={formData.shipment_type}
                    onValueChange={(value) => {
                      handleChange('shipment_type', value as ShipmentType);
                      if (isCreateMode) {
                        // Clear BL number so it regenerates with new type
                        setFormData(prev => ({ ...prev, bl_number: '' }));
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ShipmentType.INBOUND}>Inbound</SelectItem>
                      <SelectItem value={ShipmentType.OUTBOUND}>Outbound</SelectItem>
                      <SelectItem value={ShipmentType.TRANSFER}>Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Addresses */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="origin_address_id">Origin Address</Label>
                  <Select
                    value={formData.origin_address_id || 'none'}
                    onValueChange={(value) => handleChange('origin_address_id', value === 'none' ? '' : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select origin (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No origin</SelectItem>
                      {addresses.map((address) => (
                        <SelectItem key={address.id} value={address.id}>
                          {formatAddressLabel(address)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="destination_address_id">Destination Address</Label>
                  <Select
                    value={formData.destination_address_id || 'none'}
                    onValueChange={(value) => handleChange('destination_address_id', value === 'none' ? '' : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select destination (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No destination</SelectItem>
                      {addresses.map((address) => (
                        <SelectItem key={address.id} value={address.id}>
                          {formatAddressLabel(address)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="planned_departure_at">Planned Departure</Label>
                  <Input
                    id="planned_departure_at"
                    type="datetime-local"
                    value={formData.planned_departure_at}
                    onChange={(e) => handleChange('planned_departure_at', e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="planned_arrival_at">Planned Arrival</Label>
                  <Input
                    id="planned_arrival_at"
                    type="datetime-local"
                    value={formData.planned_arrival_at}
                    onChange={(e) => handleChange('planned_arrival_at', e.target.value)}
                  />
                </div>
              </div>

              {/* Product Units */}
              {formData.job_id && (
                <div className="border-t pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">
                      Product Units {isCreateMode ? '(Optional)' : `(${assetInstances.length})`}
                    </h3>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={openAddUnitsModal}
                      disabled={!isCreateMode && orderItems.length === 0}
                    >
                      {isCreateMode ? 'Select Units' : 'Add Units'}
                    </Button>
                  </div>

                  {!isCreateMode && orderItems.length === 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-yellow-800">
                        <strong>Unable to load order items.</strong> This shipment's job may not be linked to an order, or the order has no items.
                      </p>
                    </div>
                  )}

                {orderItems.length > 0 && (
                  <div className="bg-muted/30 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-semibold mb-2">Order Limits</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {orderItems.map((item) => {
                        const currentCount = assetInstances.filter(
                          inst => (inst.template?.id || inst.template_id) === item.asset_template_id
                        ).length;
                        const isComplete = currentCount >= item.quantity;

                        return (
                          <div key={item.id} className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">
                              {item.asset_template?.name || 'Product'}
                            </span>
                            <Badge variant={isComplete ? 'default' : 'secondary'}>
                              {currentCount}/{item.quantity}
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {assetInstances.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground bg-muted/30 rounded-lg">
                    No product units in this shipment yet
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {assetInstances.map((instance) => {
                      const primaryImage = instance.template?.images?.find((img: any) => img.variant === 'web');
                      const imageUrl = primaryImage?.url || instance.template?.images?.[0]?.url;

                      return (
                        <div
                          key={instance.id}
                          className="flex items-center gap-3 p-3 border rounded-lg bg-card"
                        >
                          <ProductThumbnail
                            src={imageUrl}
                            alt={instance.template?.name || 'Product'}
                            size="sm"
                            fallback={<Package className="h-6 w-6 text-muted-foreground" />}
                          />

                          <div className="flex-1 min-w-0">
                            <div className="font-medium font-mono text-sm">
                              {instance.instance_number}
                            </div>
                            {instance.template?.name && (
                              <div className="text-xs text-muted-foreground truncate">
                                {instance.template.name}
                              </div>
                            )}
                            {instance.current_location && (
                              <div className="text-xs text-muted-foreground">
                                @ {instance.current_location.city}
                              </div>
                            )}
                          </div>

                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveUnit(instance.id)}
                            className="flex-shrink-0"
                          >
                            <X className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              )}

                            {/* Info message for create mode */}
              {isCreateMode && !formData.job_id && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900">
                    <strong>Note:</strong> Select a job to optionally add product units to this shipment.
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate('/shipments')}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting
                    ? (isCreateMode ? 'Creating...' : 'Updating...')
                    : (isCreateMode ? 'Create Shipment' : 'Update Shipment')
                  }
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Add Units Modal */}
        <Dialog open={addUnitsModalOpen} onOpenChange={setAddUnitsModalOpen}>
          <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col p-0">
            <div className="px-6 pt-6 pb-4 border-b">
              <DialogHeader>
                <DialogTitle>
                  {selectUnitsForProduct
                    ? `Select Units - ${selectUnitsForProduct.asset_template?.name}`
                    : 'Select Products to Add Units'}
                </DialogTitle>
              </DialogHeader>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4" style={{ minHeight: '300px', maxHeight: 'calc(85vh - 200px)' }}>
              {!selectUnitsForProduct ? (
                /* Show products list */
                <div className="space-y-3 min-h-full">
                  {orderItems.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No products in this order. Please select a job with an order first.
                    </div>
                  ) : (
                    orderItems.map((item) => {
                      const currentCount = assetInstances.filter(
                        inst => (inst.template?.id || inst.template_id) === item.asset_template_id
                      ).length;
                      const remaining = item.quantity - currentCount;
                      const template = item.asset_template as { id: string; name: string; images?: Array<{ url?: string; variant: string }> } | undefined;
                      const primaryImage = template?.images?.find((img: any) => img.variant === 'web');
                      const imageUrl = primaryImage?.url || template?.images?.[0]?.url;

                      return (
                        <Card key={item.id} className="overflow-hidden">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-4">
                              <ProductThumbnail
                                src={imageUrl}
                                alt={item.asset_template?.name || 'Product'}
                                size="md"
                                fallback={<Package className="h-12 w-12 text-muted-foreground" />}
                              />

                              <div className="flex-1">
                                <h4 className="font-semibold text-base">
                                  {item.asset_template?.name || 'Product'}
                                </h4>
                                <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                                  <span>Order Qty: {item.quantity}</span>
                                  <span>•</span>
                                  <span className={remaining > 0 ? 'text-orange-600 font-medium' : 'text-green-600 font-medium'}>
                                    {currentCount}/{item.quantity} added
                                  </span>
                                  {remaining > 0 && (
                                    <>
                                      <span>•</span>
                                      <span className="text-primary font-medium">
                                        {remaining} remaining
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>

                              <Button
                                variant={remaining > 0 ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => openSelectUnitsForProduct(item)}
                                disabled={remaining === 0}
                              >
                                {remaining > 0 ? 'Select Units' : 'Complete'}
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </div>
              ) : (
                /* Show units for selected product */
                <div className="flex flex-col gap-4 min-h-full">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex-shrink-0">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-blue-800">
                        Showing available units for this product
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectUnitsForProduct(null)}
                      >
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Back to Products
                      </Button>
                    </div>
                  </div>

                  {availableInstances.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No available units found for this product
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2 flex-1">
                        {availableInstances.map((instance) => {
                          const primaryImage = instance.template?.images?.find((img: any) => img.variant === 'web');
                          const imageUrl = primaryImage?.url || instance.template?.images?.[0]?.url;
                          const isSelected = selectedNewUnits.includes(instance.id);
                          const templateId = instance.template?.id || instance.template_id || '';
                          const canSelect = isSelected || canSelectProduct(templateId);

                          return (
                            <div
                              key={instance.id}
                              className={`flex items-center gap-3 p-3 border rounded-lg transition-colors ${
                                isSelected
                                  ? 'bg-primary/10 border-primary'
                                  : canSelect
                                    ? 'hover:bg-muted/50 cursor-pointer'
                                    : 'opacity-50 cursor-not-allowed'
                              }`}
                              onClick={() => {
                                if (!canSelect) return;
                                setSelectedNewUnits(prev =>
                                  prev.includes(instance.id)
                                    ? prev.filter(id => id !== instance.id)
                                    : [...prev, instance.id]
                                );
                              }}
                            >
                              <Checkbox
                                checked={isSelected}
                                disabled={!canSelect}
                                onCheckedChange={() => {}}
                                className="pointer-events-none"
                              />

                              <ProductThumbnail
                                src={imageUrl}
                                alt={instance.template?.name || 'Product'}
                                size="sm"
                                fallback={<Package className="h-6 w-6 text-muted-foreground" />}
                              />

                              <div className="flex-1">
                                <div className="font-medium font-mono text-sm">
                                  {instance.instance_number}
                                </div>
                                {instance.current_location && (
                                  <div className="text-xs text-muted-foreground">
                                    @ {instance.current_location.city}
                                  </div>
                                )}
                                {!canSelect && !isSelected && (
                                  <div className="text-xs text-destructive font-medium mt-1">
                                    Order limit reached
                                  </div>
                                )}
                              </div>

                              <Badge
                                variant={
                                  instance.current_status === 'available'
                                    ? 'default'
                                    : instance.current_status === 'booked'
                                      ? 'secondary'
                                      : instance.current_status === 'rented'
                                        ? 'warning'
                                        : 'secondary'
                                }
                              >
                                {instance.current_status}
                              </Badge>
                            </div>
                          );
                        })}
                      </div>

                      {/* Pagination Controls */}
                      {modalTotalPages > 1 && (
                        <div className="flex items-center justify-between py-3 border-t flex-shrink-0 mt-4">
                          <div className="text-sm text-muted-foreground">
                            Page {modalPage} of {modalTotalPages} ({modalTotalItems} units total)
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setModalPage(1)}
                              disabled={modalPage === 1}
                            >
                              {'<<'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setModalPage(prev => Math.max(1, prev - 1))}
                              disabled={modalPage === 1}
                            >
                              {'<'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setModalPage(prev => Math.min(modalTotalPages, prev + 1))}
                              disabled={modalPage >= modalTotalPages}
                            >
                              {'>'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setModalPage(modalTotalPages)}
                              disabled={modalPage >= modalTotalPages}
                            >
                              {'>>'}
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <DialogFooter className="px-6 py-4 border-t">
              <div className="flex items-center justify-between w-full">
                <div className="text-sm text-muted-foreground">
                  {selectUnitsForProduct && `${selectedNewUnits.length} unit(s) selected`}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setSelectedNewUnits([]);
                      setSelectUnitsForProduct(null);
                      setAddUnitsModalOpen(false);
                    }}
                  >
                    Cancel
                  </Button>
                  {selectUnitsForProduct && (
                    <Button
                      type="button"
                      onClick={handleAddUnits}
                      disabled={selectedNewUnits.length === 0}
                    >
                      Add {selectedNewUnits.length > 0 && `${selectedNewUnits.length}`} Unit{selectedNewUnits.length !== 1 ? 's' : ''}
                    </Button>
                  )}
                </div>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default EditShipment;
